import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Autorenew, DeleteOutline, Edit, FileCopy, PlayArrow } from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import EmbeddedSignupButton from "./EmbeddedSignupButton";  // DATXALE_EMBEDDED_SIGNUP_20260505

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  helperBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    background: "#fafafa",
  },
  mono: { fontFamily: "monospace" },
}));

function WhatsAppApiConnectionModal({ open, onClose, initial, onSaved, metaConfig }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const isEdit = !!initial?.id;
  const schema = useMemo(() => {
      const base = {
        name: Yup.string()
          .min(2, t("whatsappApi.validation.nameMin"))
          .required(t("whatsappApi.validation.nameRequired")),
      phoneNumberId: Yup.string().required(t("whatsappApi.validation.phoneNumberIdRequired")),
      whatsappBusinessAccountId: Yup.string().required(t("whatsappApi.validation.wabaIdRequired")),
      isDefault: Yup.boolean(),
    };

    return Yup.object().shape({
      ...base,
      accessToken: isEdit
        ? Yup.string().nullable()
        : Yup.string().required(t("whatsappApi.validation.accessTokenRequired")),
    });
  }, [isEdit, t]);

  const webhookUrl = metaConfig?.webhookUrl || metaConfig?.webhookPath || "";
  const verifyToken = metaConfig?.verifyToken || "";

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("whatsappApi.toasts.copied"));
    } catch (_) {
      toast.info(text);
    }
  };


  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? t("whatsappApi.modal.editTitle") : t("whatsappApi.modal.newTitle")}</DialogTitle>
      <Formik
        initialValues={{
          name: initial?.name || "",
          phoneNumberId: initial?.phoneNumberId || "",
          whatsappBusinessAccountId: initial?.whatsappBusinessAccountId || "",
          accessToken: "",
          isDefault: !!initial?.isDefault,
        }}
        validationSchema={schema}
        enableReinitialize
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const payload = {
              name: values.name,
              phoneNumberId: values.phoneNumberId,
              whatsappBusinessAccountId: values.whatsappBusinessAccountId || null,
              isDefault: !!values.isDefault,
            };
            if (!isEdit || (values.accessToken && values.accessToken.trim())) {
              payload.accessToken = values.accessToken;
            }

            const formatMetaSubscribeReason = (conn) => {
              const raw = String(conn?.metaWabaSubscribeError || "").trim();
              if (!raw) return "";
              if (raw === "missing_whatsapp_business_account_id") return t("whatsappApi.errors.missingWabaId");
              if (raw === "missing_access_token") return t("whatsappApi.errors.missingAccessToken");
              if (raw === "app_not_subscribed") return t("whatsappApi.errors.appNotSubscribed");
              return raw;
            };

            if (isEdit) {
              const { data } = await api.put(`/whatsapp-api/${initial.id}`, payload);
              const metaFailed = data?.metaWabaSubscribed === false || String(data?.status || "").toLowerCase() === "error";
              if (metaFailed) {
                toast.error(
                  t("whatsappApi.toasts.connectionSavedButMetaSubscribeFailed", { reason: formatMetaSubscribeReason(data) })
                );
              } else {
                toast.success(t("whatsappApi.toasts.connectionUpdated"));
              }
            } else {
              const { data } = await api.post("/whatsapp-api", payload);
              const metaFailed = data?.metaWabaSubscribed === false || String(data?.status || "").toLowerCase() === "error";
              if (metaFailed) {
                toast.error(
                  t("whatsappApi.toasts.connectionSavedButMetaSubscribeFailed", { reason: formatMetaSubscribeReason(data) })
                );
              } else {
                toast.success(t("whatsappApi.toasts.connectionCreated"));
              }
            }
            onSaved?.();
            onClose?.();
          } catch (err) {
            toastError(err);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting, touched, errors }) => (
          <Form>
            <DialogContent dividers>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {t("whatsappApi.helper.webhookUrl")}
                <span style={{ marginLeft: 8 }} className={classes.mono}>
                  {webhookUrl || "/whatsapp-api/webhook/{webhookKey}"}
                </span>
                <Tooltip title={t("whatsappApi.actions.copy")}>
                  <IconButton size="small" onClick={() => webhookUrl && handleCopy(webhookUrl)} disabled={!webhookUrl}>
                    <FileCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {t("whatsappApi.helper.verifyToken")}
                <span style={{ marginLeft: 8 }} className={classes.mono}>
                  {verifyToken || "-"}
                </span>
                <Tooltip title={t("whatsappApi.actions.copy")}>
                  <IconButton size="small" onClick={() => verifyToken && handleCopy(verifyToken)} disabled={!verifyToken}>
                    <FileCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Typography>

              <Field
                as={TextField}
                name="name"
                label={t("whatsappApi.form.name")}
                variant="outlined"
                margin="dense"
                fullWidth
                error={touched.name && !!errors.name}
                helperText={touched.name && errors.name ? String(errors.name) : ""}
              />

              <Field
                as={TextField}
                name="phoneNumberId"
                label={t("whatsappApi.form.phoneNumberId")}
                variant="outlined"
                margin="dense"
                fullWidth
                error={touched.phoneNumberId && !!errors.phoneNumberId}
                helperText={
                  touched.phoneNumberId && errors.phoneNumberId
                    ? String(errors.phoneNumberId)
                    : t("whatsappApi.form.phoneNumberIdHelper")
                }
              />

              <Field
                as={TextField}
                name="whatsappBusinessAccountId"
                label={t("whatsappApi.form.wabaIdOptional")}
                variant="outlined"
                margin="dense"
                fullWidth
              />

              <Field
                as={TextField}
                name="accessToken"
                label={isEdit ? t("whatsappApi.form.accessTokenEditHint") : t("whatsappApi.form.accessToken")}
                variant="outlined"
                margin="dense"
                fullWidth
                type="password"
                error={touched.accessToken && !!errors.accessToken}
                helperText={touched.accessToken && errors.accessToken ? String(errors.accessToken) : ""}
              />

              <FormControlLabel
                control={<Field as={Switch} name="isDefault" color="primary" />}
                label={t("whatsappApi.form.useAsDefault")}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} disabled={isSubmitting}>{t("common.cancel")}</Button>
              <Button type="submit" color="primary" variant="contained" disabled={isSubmitting}>
                {isSubmitting ? t("whatsappApi.actions.saving") : t("common.save")}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}

export default function WhatsAppApiConnections() {
  const classes = useStyles();
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [connections, setConnections] = useState([]);
  const [metaConfig, setMetaConfig] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [rotateConfirmOpen, setRotateConfirmOpen] = useState(false);
  const [rotateSecretsConfirmOpen, setRotateSecretsConfirmOpen] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [metaSubscribingId, setMetaSubscribingId] = useState(null);
  // DATXALE_GAP3_TEST_BUTTON_20260505
  const [testingId, setTestingId] = useState(null);

  const webhookUrl = useMemo(() => {
    return metaConfig?.webhookUrl || metaConfig?.webhookPath || "";
  }, [metaConfig]);

  const verifyToken = useMemo(() => {
    return metaConfig?.verifyToken || "";
  }, [metaConfig]);

  const canRotateVerifyToken = useMemo(() => {
    const profile = String(user?.profile || "").toLowerCase();
    return !!user?.super || profile === "admin" || profile === "supervisor" || profile === "cliente";
  }, [user]);

  const loadMetaConfig = async () => {
    try {
      const { data } = await api.get("/whatsapp-api/meta-config");
      setMetaConfig(data || null);
    } catch (_) {
      setMetaConfig(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/whatsapp-api");
      setConnections(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadMetaConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("whatsappApi.toasts.copied"));
    } catch (_) {
      toast.info(text);
    }
  };

  const handleDelete = async () => {
    if (!deleting?.id) return;
    try {
      await api.delete(`/whatsapp-api/${deleting.id}`);
      toast.success(t("whatsappApi.toasts.connectionDeleted"));
      setConfirmOpen(false);
      setDeleting(null);
      await load();
    } catch (err) {
      toastError(err);
    }
  };

  const handleRotateVerifyToken = async () => {
    setRotating(true);
    try {
      const { data } = await api.post("/whatsapp-api/meta-config/rotate-verify-token");
      setMetaConfig(data || null);
      toast.success(t("whatsappApi.toasts.verifyTokenRotated"));
      setRotateConfirmOpen(false);
      await load();
    } catch (err) {
      toastError(err);
    } finally {
      setRotating(false);
    }
  };

  const handleRotateWebhookKeyAndVerifyToken = async () => {
    setRotating(true);
    try {
      const { data } = await api.post("/whatsapp-api/meta-config/rotate-secrets");
      setMetaConfig(data || null);
      toast.success(t("whatsappApi.toasts.secretsRotated"));
      setRotateSecretsConfirmOpen(false);
      await load();
    } catch (err) {
      toastError(err);
    } finally {
      setRotating(false);
    }
  };

  const formatMetaSubscribeReason = (conn) => {
    const raw = String(conn?.metaWabaSubscribeError || "").trim();
    if (!raw) return "";
    if (raw === "missing_whatsapp_business_account_id") return t("whatsappApi.errors.missingWabaId");
    if (raw === "missing_access_token") return t("whatsappApi.errors.missingAccessToken");
    if (raw === "app_not_subscribed") return t("whatsappApi.errors.appNotSubscribed");
    return raw;
  };

  const handleRetryMetaSubscribe = async (conn) => {
    if (!conn?.id) return;

    setMetaSubscribingId(conn.id);
    try {
      const { data } = await api.post(`/whatsapp-api/${conn.id}/meta/subscribe`);

      const ok = data?.metaWabaSubscribed === true && String(data?.status || "").toLowerCase() !== "error";
      if (ok) {
        toast.success(t("whatsappApi.toasts.metaSubscribeOk"));
      } else {
        toast.error(
          t("whatsappApi.toasts.metaSubscribeFailed", {
            reason: formatMetaSubscribeReason(data) || t("common.anErrorOccurred"),
          })
        );
      }

      await load();
    } catch (err) {
      toastError(err);
    } finally {
      setMetaSubscribingId(null);
    }
  };

  const handleTestConnection = async (conn) => {
    if (!conn?.id) return;
    setTestingId(conn.id);
    try {
      const { data } = await api.post(`/whatsapp-api/${conn.id}/test`);
      const fmt = (label, ok, error, extra) => {
        if (ok) return `${label}: OK${extra ? " - " + extra : ""}`;
        return `${label}: ${error || "error"}`;
      };
      const phoneOk = !!data?.phone_number?.ok;
      const wabaOk = !!data?.waba?.ok;
      const webhookOk = !!data?.webhook?.ok;
      const lines = [
        fmt("Telefono", phoneOk, data?.phone_number?.error,
            data?.phone_number?.display_phone_number ? `${data.phone_number.display_phone_number} (${data.phone_number.verified_name || ""})` : ""),
        fmt("WABA", wabaOk, data?.waba?.error,
            data?.waba?.templates_count != null ? `${data.waba.templates_count} plantillas` : ""),
        fmt("Webhook", webhookOk, data?.webhook?.error,
            data?.webhook?.subscribed ? "suscrito" : "sin app subscrita"),
      ];
      const allOk = phoneOk && wabaOk && webhookOk;
      if (allOk) {
        toast.success("Conexion OK - " + lines.join(" | "));
      } else {
        toast.error("Falla en conexion - " + lines.join(" | "));
      }
    } catch (err) {
      toastError(err);
    } finally {
      setTestingId(null);
    }
  };

  const metaStatusLabel = (c) => {
    if (c?.metaWabaSubscribed === true) return t("whatsappApi.values.metaSubscribed");
    if (c?.metaWabaSubscribed === false) return t("whatsappApi.values.metaError");
    return t("whatsappApi.values.metaUnknown");
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={t("whatsappApi.confirm.deleteTitle")}
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
      >
        {t("whatsappApi.confirm.deleteMessage", { name: deleting?.name || "-" })}
      </ConfirmationModal>

      <ConfirmationModal
        title={t("whatsappApi.rotateVerifyToken.title")}
        open={rotateConfirmOpen}
        onClose={() => setRotateConfirmOpen(false)}
        onConfirm={handleRotateVerifyToken}
      >
        {t("whatsappApi.rotateVerifyToken.message")}
      </ConfirmationModal>

      <ConfirmationModal
        title={t("whatsappApi.rotateSecrets.title")}
        open={rotateSecretsConfirmOpen}
        onClose={() => setRotateSecretsConfirmOpen(false)}
        onConfirm={handleRotateWebhookKeyAndVerifyToken}
      >
        {t("whatsappApi.rotateSecrets.message")}
      </ConfirmationModal>

      <WhatsAppApiConnectionModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        initial={editing}
        onSaved={load}
        metaConfig={metaConfig}
      />

      <MainHeader>
        <Title>{t("whatsappApi.title")}</Title>
        <MainHeaderButtonsWrapper>
          {/* DATXALE_EMBEDDED_SIGNUP_20260505 */}
          <EmbeddedSignupButton onConnected={() => load()} />
          <Button
            variant="outlined"
            onClick={() => webhookUrl && handleCopy(webhookUrl)}
            startIcon={<FileCopy />}
            disabled={!webhookUrl}
          >
            {t("whatsappApi.actions.copyWebhook")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setEditing(null);
              setModalOpen(true);
            }}
          >
            {t("whatsappApi.actions.newConnection")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {loading ? (
          <div style={{ padding: 24, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </div>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("whatsappApi.table.name")}</TableCell>
                <TableCell>{t("whatsappApi.table.phoneNumberId")}</TableCell>
                <TableCell>{t("whatsappApi.table.wabaId")}</TableCell>
                <TableCell align="center">{t("whatsappApi.table.meta")}</TableCell>
                <TableCell align="center">{t("whatsappApi.table.default")}</TableCell>
                <TableCell align="center">{t("whatsappApi.table.webhook")}</TableCell>
                <TableCell align="right">{t("whatsappApi.table.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {connections.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>{c.name}</TableCell>
                  <TableCell className={classes.mono}>{c.phoneNumberId || "-"}</TableCell>
                  <TableCell className={classes.mono}>{c.whatsappBusinessAccountId || "-"}</TableCell>
                  <TableCell align="center">
                    {c.metaWabaSubscribeError ? (
                      <Tooltip title={String(c.metaWabaSubscribeError)}>
                        <span>{metaStatusLabel(c)}</span>
                      </Tooltip>
                    ) : (
                      metaStatusLabel(c)
                    )}
                  </TableCell>
                  <TableCell align="center">{c.isDefault ? t("common.yes") : t("common.no")}</TableCell>
                  <TableCell align="center">
                    {c.webhookVerified ? t("whatsappApi.values.verified") : t("whatsappApi.values.pending")}
                  </TableCell>
                  <TableCell align="right">
                    {c.isActive && c.metaWabaSubscribed !== true && (
                      <Tooltip title={t("whatsappApi.tooltips.retryMetaSubscribe")}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleRetryMetaSubscribe(c)}
                            disabled={metaSubscribingId === c.id}
                          >
                            {metaSubscribingId === c.id ? (
                              <CircularProgress size={18} />
                            ) : (
                              <Autorenew fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {/* DATXALE_GAP3_TEST_BUTTON_20260505 */}
                    <Tooltip title="Probar conexion (Meta)">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleTestConnection(c)}
                          disabled={testingId === c.id}
                        >
                          {testingId === c.id ? (
                            <CircularProgress size={18} />
                          ) : (
                            <PlayArrow fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t("whatsappApi.tooltips.edit")}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditing(c);
                          setModalOpen(true);
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("whatsappApi.tooltips.delete")}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setDeleting(c);
                          setConfirmOpen(true);
                        }}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}

              {!connections.length && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    {t("whatsappApi.empty")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}

        <div className={classes.helperBox}>
          <Typography variant="subtitle2" gutterBottom>
            {t("whatsappApi.helper.title")}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {t("whatsappApi.helper.step1")}
            <br />
            {t("whatsappApi.helper.webhookUrl")}
            <br />
            <span className={classes.mono}>{webhookUrl || "/whatsapp-api/webhook/{webhookKey}"}</span>
            <Tooltip title={t("whatsappApi.actions.copy")}>
              <IconButton size="small" onClick={() => webhookUrl && handleCopy(webhookUrl)} disabled={!webhookUrl}>
                <FileCopy fontSize="small" />
              </IconButton>
            </Tooltip>
            <br />
            {t("whatsappApi.helper.verifyToken")}
            <br />
            <span className={classes.mono}>{verifyToken || "-"}</span>
            <Tooltip title={t("whatsappApi.actions.copy")}>
              <IconButton size="small" onClick={() => verifyToken && handleCopy(verifyToken)} disabled={!verifyToken}>
                <FileCopy fontSize="small" />
              </IconButton>
            </Tooltip>
            <br />
            {t("whatsappApi.helper.step2")}
            <br />
            {t("whatsappApi.helper.step3")}
            <br />
            {t("whatsappApi.helper.step4")}
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
            {t("whatsappApi.helper.user")} {user?.email || "-"}
          </Typography>

          {canRotateVerifyToken && (
            <div style={{ marginTop: 12 }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setRotateConfirmOpen(true)}
                disabled={rotating}
              >
                {rotating ? t("whatsappApi.actions.rotating") : t("whatsappApi.actions.rotateVerifyToken")}
              </Button>
              <Button
                style={{ marginLeft: 8 }}
                variant="outlined"
                color="secondary"
                onClick={() => setRotateSecretsConfirmOpen(true)}
                disabled={rotating}
              >
                {rotating ? t("whatsappApi.actions.rotating") : t("whatsappApi.actions.rotateSecrets")}
              </Button>
            </div>
          )}
        </div>
      </Paper>
    </MainContainer>
  );
}
